import { computed, ComputedRef, Ref } from 'vue';
import type { AxiosResponse } from '@libs/http';
import { CODES, useLiveQueryV2 } from '@libs/use';
import type { Cash } from '@libs/api-reference';
import {
  useUser,
  createUserChannelName,
  createNewOmsUserChannelName,
} from '@core/user';
import { isNewOms } from '@core/order';
import { createPositionChannel } from '@core/option';

import * as API from '../requests';
import { calcRemainingCash } from '../utils/calculator';
import {
  transformCashChange,
  transformTraderStateChange,
} from '../utils/transform';
import { CASH_QUERY_KEYS } from '../constants';

export default function useCashQuery(
  onSuccess?: (...arg: any[]) => Promise<void | AxiosResponse<Cash, any>>
): {
  isLoadingCash: Ref<boolean>;
  isErrorCash: Ref<boolean>;
  cashData: ComputedRef<Cash>;
  getCash: ReturnType<typeof useLiveQueryV2>['execute'];
  cashStatus: ReturnType<typeof useLiveQueryV2>['status'];
  remainingCash: ComputedRef<number>;
  customerRemaining: ComputedRef<number>;
} {
  const { user, isUserReady } = useUser();

  const {
    result,
    isPending: isLoadingCash,
    isError: isErrorCash,
    execute: getCash,
    status: cashStatus,
  } = useLiveQueryV2<Cash>({
    immediate: isUserReady,
    fetcher: () => API.getCashAsset(user?.value?.investorBourseCodes[0]?.id),
    key: CASH_QUERY_KEYS.cash,
    unsubscribe: false,
    getChannels() {
      return [
        createUserChannelName(user.value?.userName),
        createNewOmsUserChannelName(user?.value!.bimCode),
        createPositionChannel(user?.value!.bimCode),
      ];
    },
    onUpdate: function (signal) {
      const actions = {
        [CODES.OmsAccountStateChangeSignal]: transformCashChange,
        [CODES.OmsNewAccountStateChangeSignal]: transformCashChange,
        [CODES.TraderStateChanged]: transformTraderStateChange,
      };

      if (actions[signal.code]) {
        const data: ReturnType<typeof transformCashChange> = actions[
          signal.code
        ](signal.data);

        if (
          isNewOms.value ||
          user.value?.investorBourseCodes[0].id === data.investorBourseCodeId
        ) {
          if (result.value) {
            Object.assign(result.value.data, data);

            return;
          }
        }
      }
    },
    onSuccess,
  });
  const remainingCash = computed<number>(() =>
    isNewOms.value
      ? result.value?.data?.t2 ?? 0
      : calcRemainingCash(
          result.value?.data.buyingPower,
          result.value?.data.blockedValue
        )
  );

  const customerRemaining = computed(() => {
    return isNewOms.value
      ? result.value?.data?.t2 ?? 0
      : calcRemainingCash(
          result.value?.data.nonWithdrawableMoneyRemain,
          result.value?.data.withdrawableMoneyRemain
        );
  });

  const cashData = computed<Cash | any>(() => result.value?.data || {});

  return {
    isLoadingCash,
    cashData,
    getCash,
    remainingCash,
    customerRemaining,
    isErrorCash,
    cashStatus,
  };
}
